"use client";

import { Fragment } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";
import { getAssetUrl } from "@/utils/assetUrl";

const MOTIVATIONS_TRANSLATION_MAP: Record<string, string> = {
  "partner-snoring": "concerns-about-snoring",
  "low-energy-fatigue": "low-energy",
  "ongoing-health-issues": "existing-health-concerns",
  "doctors-recommendation": "doctors-recommendation",
  "work-productivity": "work-performance",
  "sudden-event": "sudden-event",
};

export default function MotivationalMessageSection() {
  const t = useTranslations("results-page.motivational-message-section");
  const { answers } = useAssessment();

  const motivationAnswer = answers.find(
    (a) => a.questionId === "sleep-motivation",
  );

  // Parse the motivations answer for users selections, pull out "other"
  const selectedMotivations = (
    Array.isArray(motivationAnswer?.answerId) ? motivationAnswer.answerId : []
  ).filter((id) => id !== "other" && id in MOTIVATIONS_TRANSLATION_MAP);

  // Split the last answer of the motivations array out
  const leading = selectedMotivations.slice(0, -1);
  const last = selectedMotivations[selectedMotivations.length - 1];

  const getMotivationLabel = (id: string) =>
    t(`motivations.${MOTIVATIONS_TRANSLATION_MAP[id]}`);

  const renderBody = () => {
    // Render the fallback if the user only selected "other"
    if (selectedMotivations.length === 0) return t("body-fallback");

    // Render the single answer string if the user only selected one motivation
    if (selectedMotivations.length === 1) {
      return t.rich("body-single", {
        motivation: () => <strong>{getMotivationLabel(last)}</strong>,
      });
    }

    // Render the multiple answer string if the user selected multiple non "other" answers
    return t.rich("body-multiple", {
      leading: () =>
        leading.map((id, i) => (
          <Fragment key={id}>
            {i > 0 && ", "}
            <strong>{getMotivationLabel(id)}</strong>
          </Fragment>
        )),
      last: () => <strong>{getMotivationLabel(last)}</strong>,
    });
  };

  return (
    <section className="motivational-message-section">
      <div className="motivational-message-section-arc" />

      <div className="motivational-message-section-icon">
        <Image
          src={getAssetUrl("/images/results/moon_stars_icon.svg")}
          alt=""
          width={56}
          height={47}
          aria-hidden="true"
          unoptimized
        />
      </div>

      <h2 className="headline motivational-message-section-headline">
        {t("headline")}
      </h2>

      <p className="motivational-message-section-body">{renderBody()}</p>
    </section>
  );
}
